import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";

export function CompanyStampBadge() {
  const [stamp, setStamp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/hr/company-stamp");
      setStamp(data?.value_b64 || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const upload = (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Choose an image"); return; }
    const reader = new FileReader();
    reader.onload = async (e) => {
      setLoading(true);
      try {
        await api.put("/hr/company-stamp", { value_b64: e.target.result });
        toast.success("Company stamp updated");
        await refresh();
      } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };
  const clear = async () => {
    setLoading(true);
    try {
      await api.put("/hr/company-stamp", { value_b64: null });
      setStamp(null);
      toast.success("Stamp cleared");
    } catch { /* ignore */ }
    setLoading(false);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)}
        className="brut-btn-secondary px-2 py-1.5 text-[10px] inline-flex items-center gap-1"
        data-testid="company-stamp-badge">
        {stamp
          ? <img src={stamp} alt="Stamp" className="w-6 h-6 object-contain" />
          : <span className="w-6 h-6 border border-dashed border-zinc-500 rounded-full" />}
        STAMP
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 brut-card bg-white p-3 z-40 w-64" onClick={(e) => e.stopPropagation()}>
          <div className="label-mono mb-2">COMPANY STAMP</div>
          {stamp
            ? <img src={stamp} alt="Current stamp" className="max-h-24 w-full object-contain border border-zinc-300 bg-white mb-2" />
            : <div className="text-[10px] font-mono text-zinc-500 mb-2 italic">No stamp uploaded yet. Uploading here applies it to every HR letter automatically.</div>}
          <label className="brut-btn-secondary px-2 py-1 text-[10px] block cursor-pointer text-center bg-emerald-100 hover:bg-emerald-200" data-testid="stamp-upload-org">
            {loading ? "Uploading..." : "UPLOAD / REPLACE STAMP"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {stamp && (
            <button onClick={clear} className="mt-2 text-[10px] font-mono text-rose-700 hover:underline w-full text-center" data-testid="stamp-clear-org">
              Clear stamp
            </button>
          )}
        </div>
      )}
    </div>
  );
}



export function SignatureCapture({ isHR }) {
  const [tab, setTab] = useState("draw");   // draw | upload | saved
  const [preview, setPreview] = useState(null);
  const [savedSig, setSavedSig] = useState(null);
  const [library, setLibrary] = useState([]);
  const [stampPreview, setStampPreview] = useState(null);
  const [savedToProfile, setSavedToProfile] = useState(false);

  // Load user's saved signature + (HR/admin) the shared signature library
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hr/my-signature");
        setSavedSig(data?.value_b64 || null);
      } catch { /* ignore — best-effort refresh */ }
      if (isHR) {
        try {
          const { data } = await api.get("/hr/signatures");
          setLibrary(data?.signatures || []);
        } catch { /* non-admins get 403 — fine */ }
      }
    })();
    // reset any previous session state on mount
    window.__sig = null; window.__stamp = null; window.__sigSave = false;
  }, [isHR]);

  const removeFromLibrary = async (sigId) => {
    try {
      await api.delete(`/hr/signatures/${sigId}`);
      setLibrary((l) => l.filter((s) => s.id !== sigId));
      toast.success("Signature removed from library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const commitSig = (b64) => {
    setPreview(b64);
    window.__sig = b64;
  };
  const commitStamp = (b64) => {
    setStampPreview(b64);
    window.__stamp = b64;
  };
  const handleFile = (file, cb) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    const reader = new FileReader();
    reader.onload = (e) => cb(e.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">SIGNATURE</div>
        <div className="flex gap-1" data-testid="sig-tabs">
          {[
            ["draw", "DRAW"],
            ["upload", "UPLOAD"],
            ["saved", "SAVED"],
          ].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={"px-2 py-0.5 text-[10px] font-mono border-2 border-zinc-900 " + (tab === k ? "bg-emerald-300" : "bg-white hover:bg-zinc-100")}
              data-testid={`sig-tab-${k}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {tab === "draw" && (
        <SignaturePad onChange={(b64) => { commitSig(b64); }} />
      )}
      {tab === "upload" && (
        <div>
          <label className="brut-card bg-zinc-50 hover:bg-emerald-50 p-3 flex items-center gap-2 cursor-pointer text-xs font-mono" data-testid="sig-upload-btn">
            <span>Choose PNG / JPG signature image →</span>
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0], commitSig)} />
          </label>
          {preview && <img src={preview} alt="Signature preview" className="mt-2 max-h-24 border border-zinc-300 bg-white" data-testid="sig-upload-preview" />}
          <div className="mt-2 text-[10px] font-mono text-zinc-500 leading-relaxed" data-testid="sig-upload-hint">
            BEST RESULTS: sign with a dark pen on plain white paper, photograph straight-on. Any size up to 2MB — ideal ~1000×400px. PNG or JPG both work: the background is removed, ink is enhanced &amp; auto-fitted to the signature area automatically.
          </div>
        </div>
      )}
      {tab === "saved" && (
        <div className="space-y-3">
          {isHR && library.length > 0 && (
            <div>
              <div className="text-[10px] font-mono text-zinc-500 mb-1">SIGNATURE LIBRARY — auto-saved from every signed document, shared with all admins</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="sig-library-grid">
                {library.map((s) => (
                  <div key={s.id}
                    className={"relative border-2 p-1 bg-white cursor-pointer group " + (preview === s.value_b64 ? "border-emerald-500" : "border-zinc-300 hover:border-zinc-900")}
                    onClick={() => commitSig(s.value_b64)}
                    data-testid={`sig-library-item-${s.id}`}>
                    <img src={s.value_b64} alt="Saved signature" className="h-14 w-full object-contain" />
                    {preview === s.value_b64 && (
                      <div className="absolute top-0.5 left-0.5 bg-emerald-500 text-white text-[9px] font-mono px-1">SELECTED</div>
                    )}
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); removeFromLibrary(s.id); }}
                      className="absolute top-0.5 right-0.5 hidden group-hover:block bg-red-600 text-white text-[9px] font-mono px-1"
                      title="Remove from library"
                      data-testid={`sig-library-delete-${s.id}`}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {savedSig && (
            <div>
              <div className="text-[10px] font-mono text-zinc-500 mb-1">MY PROFILE SIGNATURE</div>
              <div className={"inline-block border-2 p-1 bg-white cursor-pointer " + (preview === savedSig ? "border-emerald-500" : "border-zinc-300 hover:border-zinc-900")}
                onClick={() => commitSig(savedSig)} data-testid="sig-use-saved">
                <img src={savedSig} alt="Saved signature" className="max-h-20" />
                {preview === savedSig && <div className="text-[10px] font-mono text-emerald-700">SELECTED ✓</div>}
              </div>
            </div>
          )}
          {!savedSig && (!isHR || library.length === 0) && (
            <div className="text-xs text-zinc-500 italic">No saved signatures yet. {isHR ? "Signatures are saved here automatically after you sign a document." : "Upload or draw one and tick \u201cSave to my profile\u201d below."}</div>
          )}
        </div>
      )}

      {/* Save-to-profile checkbox (except when reusing existing saved sig) */}
      {(tab === "draw" || tab === "upload") && (
        <label className="flex items-center gap-2 mt-2 text-[11px] font-mono cursor-pointer">
          <input type="checkbox" defaultChecked={false}
            onChange={(e) => { window.__sigSave = e.target.checked; setSavedToProfile(e.target.checked); }}
            data-testid="sig-save-to-profile" />
          <span>Save to my profile for one-click reuse later</span>
        </label>
      )}

      {/* HR-only: optional per-document stamp override */}
      {isHR && (
        <div className="mt-3 pt-3 border-t border-dashed border-zinc-300">
          <div className="label-mono mb-1">COMPANY STAMP (optional override for this letter)</div>
          <label className="brut-card bg-zinc-50 hover:bg-amber-50 p-2 flex items-center gap-2 cursor-pointer text-[11px] font-mono" data-testid="stamp-upload-btn">
            <span>Choose stamp image →</span>
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0], commitStamp)} />
          </label>
          {stampPreview && (
            <div className="mt-2 flex items-center gap-2">
              <img src={stampPreview} alt="Stamp preview" className="max-h-20 border border-zinc-300 bg-white" data-testid="stamp-preview" />
              <button type="button" onClick={() => commitStamp(null)}
                className="text-[10px] font-mono text-rose-700 hover:underline">Clear</button>
            </div>
          )}
          <div className="text-[10px] font-mono text-zinc-500 mt-1">Leave blank to use the org-wide default stamp (Settings → Company Stamp).</div>
        </div>
      )}
    </div>
  );
}



export function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    const p = getPos(e, canvasRef.current);
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e, canvasRef.current);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange(null);
  };
  return (
    <div>
      <canvas ref={canvasRef} width={600} height={100}
        className="border-2 border-dashed border-zinc-400 bg-white w-full touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas" />
      <button onClick={clear} className="text-[10px] font-mono text-zinc-500 hover:text-rose-700 mt-1" data-testid="sig-clear">Clear</button>
    </div>
  );
}

export function getPos(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
}
